import { useQueryClient } from '@tanstack/react-query';
import { FormikHelpers } from 'formik';
import { LucideX } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useChatChanges } from '@/app/providers/chat-changes-provider/chat-changes-provider';
import { billingQueryKeys } from '@/entities/billing';
import { useGetReportItemsGraph } from '@/entities/reports';
import { AddIntegrations } from '@/features/add-integrations';
import { BillingUserMessage } from '@/features/billing-warning';
import { IMessage } from '@/shared/api/types/chat-message';
import { env } from '@/shared/lib';
import { getLocalStorageItem } from '@/shared/utils';
import { MarkdownDndItem } from '@/widgets/conversation-reports-graph/types';

import MessagesList from './components/messages-list/messages-list';
import QuestionForm from './components/question-form/question-form';
import { useChatSocket } from './hooks/useChatSocket';
import {
  CHAT_BILLING_BLOCKED_TEXT,
  mapBillingStatusToChatEvent,
} from './model/map-billing-status-to-chat-event';
import Box from './ui/box';
import Button from './ui/button';
import ChatAnswerBlock from './ui/chat-answer-block';
import ChatAnswerBlockHeader from './ui/chat-answer-block-header';
import ChatInputBlock from './ui/chat-input-block';
import Typography from './ui/typography';
// import { TypographyP } from '../typography-p';
// import { wsSingleton } from './utils/ws-singleton';

const showChatIntegrations = env.VITE_SHOW_CHAT_INTEGRATIONS === 'true';
const WS_URL_MEETING_PROTOCOLS = env.VITE_WS_URL_MEETING_PROTOCOLS;
interface ChatProps {
  isOpen: boolean;
  onClose: () => void;
  wsUrlBase?: string;
  skipGraphCheck?: boolean;
}

type AiBillingGateState =
  | { kind: 'none' }
  | { kind: 'warning'; text: string }
  | { kind: 'blocked'; text: string };

const Chat = ({ isOpen, onClose, wsUrlBase, skipGraphCheck }: ChatProps) => {
  const queryClient = useQueryClient();
  const { isAnimating } = useChatChanges();
  const {
    meetingGroupById: meetingGroupId,
    guildId: workspaceId,
    sessionId: conversationId,
  } = useParams();
  const token = getLocalStorageItem('token');
  const { isError: isGraphError } = useGetReportItemsGraph(conversationId);
  // const wsUrl = `wss://protocolist-server.flowai.ru/mocks/ws/meeting-protocols-mock/${conversationId}?token=${token}`;
  const wsUrl = useMemo(() => {
    if (!WS_URL_MEETING_PROTOCOLS || (!showChatIntegrations ? !conversationId : false) || !token)
      return null;
    return `${wsUrlBase ?? WS_URL_MEETING_PROTOCOLS}/${conversationId}?token=${token}`;
  }, [WS_URL_MEETING_PROTOCOLS, !showChatIntegrations && conversationId, token]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [replyTo, setReplyTo] = useState<IMessage | null>(null);
  const [selections, setSelections] = useState<Record<string, Record<string, boolean>>>({});
  const [replyDraft, setReplyDraft] = useState<MarkdownDndItem[]>([]);
  const [aiBillingGate, setAiBillingGate] = useState<AiBillingGateState>({ kind: 'none' });
  const blockedNoticeShownRef = useRef(false);

  useEffect(() => {
    setAiBillingGate({ kind: 'none' });
    blockedNoticeShownRef.current = false;
    setMessages([]);
    setSelections({});
    setReplyTo(null);
    setReplyDraft([]);
    setIsThinking(false);
    setIsProcessing(false);
  }, [conversationId]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();

    const raw = e.dataTransfer.getData('application/json');
    if (!raw) return;

    const payload = JSON.parse(raw);

    if (payload.type !== 'protocol-fields') return;

    setReplyDraft((prev) => {
      const next = [...prev, ...payload.items];
      return next.filter(
        (v, i, arr) =>
          i ===
          arr.findIndex(
            (x) => x.entityId === v.entityId && x.field === v.field && x.value === v.value,
          ),
      );
    });
  };

  const normalizeHistory = (messages: IMessage[]): IMessage[] => {
    return messages.map((msg) => ({
      ...msg,
      payload: {
        ...msg.payload,
        resolved: true,
      },
      sender: msg.role === 'user' ? 'user' : 'assistant',
      createdAt: new Date(msg.timestamp as string).getTime(),
    }));
  };

  const isBillingBlockedNotice = (msg: IMessage): boolean => {
    if (msg.payload?.system_notice === 'billing_blocked') {
      return true;
    }

    return (
      msg.sender === 'assistant' &&
      msg.type === 'text' &&
      msg.payload?.text === CHAT_BILLING_BLOCKED_TEXT
    );
  };

  const appendBlockedAssistantMessageWithDedupe = useCallback(() => {
    setMessages((prev) => {
      if (blockedNoticeShownRef.current) {
        return prev;
      }

      blockedNoticeShownRef.current = true;

      return [
        ...prev,
        {
          id: uuidv4(),
          sender: 'assistant',
          type: 'text',
          payload: {
            text: CHAT_BILLING_BLOCKED_TEXT,
            system_notice: 'billing_blocked',
          },
          createdAt: Date.now(),
        },
      ];
    });
  }, []);

  const handleServerMessage = useCallback(
    (data: any) => {
      if (data?.final === true) {
        setIsProcessing(false);
      }

      if (data.type === 'history' && Array.isArray(data.messages)) {
        const normalized = normalizeHistory(data.messages);
        const hasBlockedNoticeInHistory = normalized.some(isBillingBlockedNotice);
        blockedNoticeShownRef.current = hasBlockedNoticeInHistory;

        if (hasBlockedNoticeInHistory) {
          setAiBillingGate({ kind: 'blocked', text: CHAT_BILLING_BLOCKED_TEXT });
        }

        setMessages(normalized);

        // инициализация selections из истории
        const selectionsFromHistory: Record<string, Record<string, boolean>> = {};

        normalized.forEach((msg) => {
          if ((msg.type === 'plan' || msg.type === 'plan_update') && msg.payload?.items) {
            const initial: Record<string, boolean> = {};
            msg.payload.items.forEach((item: any) => {
              initial[item.id] = item.checked;
            });
            selectionsFromHistory[msg.id as string] = initial;
          }

          if (msg.type === 'edit' && msg.payload?.changes) {
            const initial: Record<string, boolean> = {};
            msg.payload.changes.forEach((c: any) => {
              initial[c.id] = c.checked;
            });
            selectionsFromHistory[msg.id as string] = initial;
          }
        });

        setSelections(selectionsFromHistory);
        return;
      }

      if (data.type === 'thinking') {
        setIsThinking(Boolean(data.payload?.active));
        return;
      }

      if (data.type === 'status') {
        const billingEvent = mapBillingStatusToChatEvent(data.payload);
        if (billingEvent.kind !== 'none') {
          setIsProcessing(false);
          if (billingEvent.kind === 'blocked') {
            setAiBillingGate({ kind: 'blocked', text: CHAT_BILLING_BLOCKED_TEXT });
            appendBlockedAssistantMessageWithDedupe();
          } else {
            setAiBillingGate((prev) =>
              prev.kind === 'blocked' ? prev : { kind: 'warning', text: billingEvent.text },
            );
          }

          queryClient.invalidateQueries({ queryKey: billingQueryKeys.overview() });
          return;
        }
      }

      setMessages((prev) => [
        ...prev,
        {
          ...data,
          sender: 'assistant',
          createdAt: Date.now(),
        },
      ]);

      // selections для live-сообщений
      if ((data.type === 'plan' || data.type === 'plan_update') && data.payload?.items) {
        const initial: Record<string, boolean> = {};
        data.payload.items.forEach((item: any) => {
          initial[item.id] = item.checked;
        });

        setSelections((prev) => ({
          ...prev,
          [data.id as string]: initial,
        }));
      }

      if (data.type === 'edit' && data.payload?.changes) {
        const initial: Record<string, boolean> = {};
        data.payload.changes.forEach((c: any) => {
          initial[c.id] = c.checked;
        });

        setSelections((prev) => ({
          ...prev,
          [data.id as string]: initial,
        }));
      }
    },
    [appendBlockedAssistantMessageWithDedupe, queryClient],
  );

  // OPEN WS
  const wsRef = useChatSocket({
    isOpen,
    wsUrl,
    onMessage: handleServerMessage,
  });

  const clientId = useMemo(() => uuidv4(), []);

  const handleSendMessage = (
    values: { query: string },
    { resetForm }: FormikHelpers<{ query: string }>,
  ) => {
    if (aiBillingGate.kind === 'blocked') return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    const payload: any = {
      text: values.query,
      protocol_id: conversationId,
      client_id: clientId,
    };

    // console.log('payload', payload);

    if (replyTo) {
      payload.reply_to = replyTo.id;
      payload.reply_to_type = replyTo.type;

      const selected = selections[replyTo.id as string];
      if (selected) {
        payload.selected = Object.entries(selected)
          .filter(([, v]) => v)
          .map(([k]) => k);
      }
    }

    if (replyDraft.length > 0) {
      payload.entities = replyDraft.map((node) => ({ id: node.entityId, field: node.field }));
    }

    wsRef.current.send(JSON.stringify(payload));

    setMessages((prev) => [
      ...prev,
      {
        id: uuidv4(),
        sender: 'user',
        type: 'user',
        payload: {
          text: values.query,
          reply_to: replyTo?.id ?? null,
          entities: replyDraft.map((node) => ({
            entityId: node.entityId,
            field: node.field,
            value: node.value,
          })),
        },
        createdAt: Date.now(),
      },
    ]);

    setReplyTo(null);

    // console.log('REPLY DRAFT:', replyDraft);
    // В будущем:
    // payload.fragments = replyDraft;
    setReplyDraft([]);

    resetForm();

    setIsProcessing(true);
  };

  const handleKeyDown = (event: KeyboardEvent, submitForm: () => void) => {
    if (isThinking || isAnimating || isProcessing || aiBillingGate.kind === 'blocked') return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitForm();
    }
  };

  const getReplyPreviewText = (msg: IMessage) => {
    if (!msg) return '';

    if (msg.payload?.text) return msg.payload.text;

    if (msg.type === 'plan') return 'План изменений';
    if (msg.type === 'plan_update') return 'Обновлённый план';
    if (msg.type === 'progress') return msg.payload?.text ?? 'Статус выполнения';

    return 'Сообщение';
  };

  // INFO BLOCK
  const pendingInfoRef = useRef(false);
  const lastInfoRef = useRef<string | null>(null);

  const trySendInfo = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      pendingInfoRef.current = true;
      return;
    }

    const infoKey = JSON.stringify({
      meetingGroupId,
      workspaceId,
      conversationId,
    });

    if (lastInfoRef.current === infoKey) return;

    lastInfoRef.current = infoKey;
    pendingInfoRef.current = false;

    ws.send(
      JSON.stringify({
        type: 'info',
        payload: {
          client_id: clientId,
          route:
            conversationId && workspaceId && meetingGroupId
              ? 'conversation'
              : meetingGroupId
                ? 'meeting-group'
                : '/',
          meeting_group: meetingGroupId ?? null,
          workspace: workspaceId ?? null,
          conversation: conversationId ?? null,
        },
      }),
    );

    // console.log('[WS] info sent');
  }, [clientId, meetingGroupId, workspaceId, conversationId]);

  useEffect(() => {
    // if (showChatIntegrations) {
    trySendInfo();
    // }
  }, [meetingGroupId, workspaceId, conversationId, trySendInfo /* , showChatIntegrations */]);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;

    const handleOpen = () => {
      if (pendingInfoRef.current /* && showChatIntegrations */) {
        trySendInfo();
      }
    };

    ws.addEventListener('open', handleOpen);
    return () => ws.removeEventListener('open', handleOpen);
  }, [trySendInfo /* , showChatIntegrations */]);

  useEffect(() => {
    if (showChatIntegrations) {
      console.log(WS_URL_MEETING_PROTOCOLS);
      console.log(wsUrl);
    }
  }, [showChatIntegrations, WS_URL_MEETING_PROTOCOLS, wsUrl]);

  return (
    <Rnd
      default={{
        x: window.innerWidth - 420,
        y: window.innerHeight - 520,
        width: 420,
        height: 520,
      }}
      bounds="parent"
      minWidth={350}
      minHeight={300}
      dragHandleClassName="handler"
      style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 100 }}
      onDragOver={(e: any) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <ChatAnswerBlock className="h-full">
        <ChatAnswerBlockHeader className="handler">
          <Box className="flex items-center gap-1">
            <Typography variant="h6">Чат с протоколом</Typography>
            {showChatIntegrations && <AddIntegrations />}
          </Box>
          <Button onClick={onClose} className="!bg-transparent">
            <LucideX />
          </Button>
        </ChatAnswerBlockHeader>

        <Box className="flex-1 overflow-y-auto">
          {conversationId && isGraphError && !skipGraphCheck ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 p-6 text-center">
              <span className="text-4xl">📄</span>
              <p className="text-sm text-gray-500">Протокол не найден. Чат недоступен.</p>
            </div>
          ) : (
            <MessagesList
              protocol_id={conversationId as string}
              messages={messages}
              thinking={isThinking}
              onReply={setReplyTo}
              selections={selections}
              setSelections={setSelections}
              ws={wsRef}
              setMessages={setMessages}
              isProcessing={isProcessing}
            />
          )}
        </Box>

        {!(conversationId && isGraphError && !skipGraphCheck) && (
          <ChatInputBlock>
            {aiBillingGate.kind === 'warning' && (
              <div className="px-3 pt-2">
                <BillingUserMessage variant="warning" message={aiBillingGate.text} />
              </div>
            )}
            {(replyTo || replyDraft.length > 0) && (
              <Box className="px-3 py-2 bg-[#F3F4F6] rounded-lg flex flex-col items-start gap-2">
                {replyTo && (
                  <Box className="flex items-center justify-between w-full">
                    <Box className="flex flex-col overflow-hidden">
                      <span className="text-xs text-gray-500 font-medium">Ответить на:</span>
                      <span
                        className="text-sm text-gray-700 truncate max-w-full"
                        title={getReplyPreviewText(replyTo)}
                      >
                        {getReplyPreviewText(replyTo)}
                      </span>
                    </Box>
                    <button
                      onClick={() => setReplyTo(null)}
                      className="text-gray-400 hover:text-red-500 text-sm px-2"
                    >
                      ✕
                    </button>
                  </Box>
                )}
                {replyDraft.length > 0 && (
                  <Box className="flex items-center justify-between w-full">
                    <Box className="flex flex-col overflow-hidden">
                      <span className="text-xs text-gray-500 font-medium">
                        Перетащенные фрагменты:
                      </span>

                      <ul className="text-sm text-gray-700 list-disc pl-4">
                        {replyDraft.map((item, i) => (
                          <li key={`${item.entityId}:${item.field}:${i}`} className="truncate">
                            {item.value}
                          </li>
                        ))}
                      </ul>
                    </Box>
                    <button
                      onClick={() => setReplyDraft([])}
                      className="text-gray-400 hover:text-red-500 text-sm px-2"
                    >
                      ✕
                    </button>
                  </Box>
                )}
              </Box>
            )}

            <QuestionForm
              isProcessing={
                isThinking || isAnimating || isProcessing || aiBillingGate.kind === 'blocked'
              }
              handleSubmit={handleSendMessage}
              handleKeyDown={handleKeyDown}
            />
          </ChatInputBlock>
        )}
      </ChatAnswerBlock>
    </Rnd>
  );
};

export default Chat;
